<script lang="ts" setup>
import { OtMarketId } from '@1trading/design-system';
import { computed, type PropType } from 'vue';

import { MarketType } from '@exchange/libs/market/service/src/lib/market-model';
import { OrderAmount, OrderPrice, OrderSide, OrderTime, OrderStatus as OrderStatusToDisplay, OrderTotal, OrderType } from '@exchange/libs/order/shared-components/src';
import { MyOrdersListType } from '@exchange/libs/order/shared-model/src/lib/order-essentials';
import type { OrderWithTradesModel } from '@exchange/libs/order/shared-model/src/lib/order-model';

import DivTableCell from './DivTableCell.vue';
import DivTableRow from './DivTableRow.vue';
import OrderCloseButton from './OrderCloseButton.vue';
import useOrder from '../useOrder';

const props = defineProps({
  cancelOrder: { type: Function as PropType<(id: string) => Promise<void>>, required: true },
  isCurrentMarketOrder: { type: Boolean, required: true },
  order: { type: Object as PropType<OrderWithTradesModel>, required: true },
  showOrderType: { type: Boolean, required: true },
  type: { type: String as PropType<MyOrdersListType>, required: true },
});

const { animation, market, marketIsInMaintenance, marketIsSuspended, myOrdersSettings, showTimestamp } = useOrder(props.order, props.type);

const isOpen = computed(() => props.type === MyOrdersListType.open);
const orderItemClasses = computed(() => ({
  // For Filled and Open orders
  'animation-running': myOrdersSettings.value.allowAnimations ? animation.value : '',
  'orders-item-desktop--animated': myOrdersSettings.value.allowAnimations,
  [props.order.isBuy ? 'buy' : 'sell']: true,
  [props.type === 'open' ? 'orders-item-desktop--open' : 'orders-item-desktop--filled']: true,
  'status-filled-closed': props.order.status === 'FILLED_CLOSED',
  'status-filled-fully': props.order.status === 'FILLED_FULLY',
  'status-filled-rejected': props.order.status === 'FILLED_REJECTED',

  // Open orders only
  'market-suspended': marketIsSuspended.value,
  'market-in-maintenance': marketIsInMaintenance.value,
  'status-open': props.order.status === 'OPEN',
  'status-triggered': props.order.status === 'STOP_TRIGGERED',
  'status-filled': props.order.status === 'FILLED',
  'status-closing': props.order.status === 'CLOSING',
  'status-closed': props.order.status === 'CLOSED',
  'status-rejected': props.order.status === 'REJECTED',
  'status-failed': props.order.status === 'FAILED',
}));
</script>

<template>
  <div-table-row
    class="orders-item-desktop"
    :class="orderItemClasses"
    :style="{ 'animation-name': animation }"
    :data-id="order.id"
  >
    <div-table-cell class="orders-item-desktop__cell time">
      <order-time
        :order="order"
        :force-absolute-time="showTimestamp"
        :list-type="type"
      />
    </div-table-cell>
    <div-table-cell class="orders-item-desktop__cell market-id">
      <ot-market-id
        :market-id="order.marketName"
        :is-link="false"
        :instrument-code="order.instrumentCode"
      />
      <ot-lozenge
        v-if="market?.type === MarketType.PERP"
        :filled="false"
        :text="$t('fundamentals.perpetual.short')"
        class="orders-item-desktop__lozenge"
      />
    </div-table-cell>
    <div-table-cell class="orders-item-desktop__cell compact-cell">
      <order-side :is-buy="order.isBuy" />
    </div-table-cell>
    <div-table-cell
      v-if="showOrderType"
      class="orders-item-desktop__cell type"
    >
      <order-type
        :order="order"
        :market="market"
      />
    </div-table-cell>
    <div-table-cell class="orders-item-desktop__cell has-currency">
      <order-amount
        :order="order"
        :market="market"
        :list-type="type"
      />
    </div-table-cell>
    <div-table-cell class="orders-item-desktop__cell has-currency">
      <order-price
        :order="order"
        :market="market"
      />
    </div-table-cell>
    <div-table-cell class="orders-item-desktop__cell total has-currency">
      <order-total
        :order="order"
        :market="market"
        :list-type="type"
      />
    </div-table-cell>
    <div-table-cell
      v-if="isOpen"
      class="orders-item-desktop__cell status"
    >
      <order-status-to-display
        :order="order"
        :market="market"
      />
      <div class="orders-item-desktop__close">
        <order-close-button
          v-if="isOpen"
          variant="desktop"
          :order="order"
          :market-in-maintenance="marketIsInMaintenance"
          :cancel-order="cancelOrder"
        />
      </div>
    </div-table-cell>
  </div-table-row>
</template>

<style lang="scss">
.orders-item-desktop {
  height: 44px;
  border-radius: var(--app-border-radius);
  color: rgb(var(--v-theme-text-secondary));
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-regular);

  &.orders-item-desktop--animated {
    animation-duration: 1s;
    animation-fill-mode: forwards;
    animation-timing-function: ease-in;
    transition: all 200ms ease-in;
  }

  &:hover {
    background-color: rgb(var(--v-theme-elevation-1));
  }

  &__cell {
    white-space: nowrap;

    .side {
      display: inline-block;
      padding-left: 4px;
      text-transform: uppercase;
    }

    &.market-id {
      display: flex;
      align-items: center;
    }

    &.compact-cell {
      max-width: 50px;
    }

    &.type {
      --type-short-display: none;
      --type-long-display: inline;

      .type__short {
        display: var(--type-short-display);
      }

      .type__long {
        display: var(--type-long-display);
      }
    }

    &.has-currency {
      --currency-display: inline;

      .ot-number-display__suffix {
        display: var(--currency-display);
      }
    }

    &.has-currency,
    &.time {
      text-align: left;
    }

    &.side {
      text-align: center;
    }

    &.status {
      position: relative;

      /* for close button (absolutely placed) */
    }

    .order-status {
      overflow: hidden;
      max-width: 90%;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .compact-side & .side,
    .hide-time &.time {
      display: none;
    }

    .compact-order-type &.type {
      --type-short-display: inline;
      --type-long-display: none;
    }

    .hide-currency &.has-currency {
      --currency-display: none;
    }

    /* truncate status for small viewports
  ** (prevent hiding close button) */
    .compact-status &.status {
      .order-status {
        overflow: hidden;
        max-width: 100%;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }
  }

  &__close {
    position: absolute;
    top: 0;
    right: 5px;
    display: flex;
    height: 100%;
    align-items: center;
    justify-content: center;
  }

  &__lozenge {
    margin-left: 4px;
  }
}

.orders-item-desktop--filled {
  &.hide-time .total {
    padding-right: 10px;
  }
}
</style>
